import { ApiResponse, Document, DocumentRelatedItem, DocumentVersion } from '../types'
import { apiService } from './api.service'

// Map rule_type to filtervar (same as React)
function getFilterVar(ruleType: number): string | null {
  const filterVarMap: { [key: number]: string } = {
    // Tags
    6: 'tags__id__all', // FILTER_HAS_TAGS_ALL
    7: 'is_tagged', // FILTER_HAS_ANY_TAG
    17: 'tags__id__none', // FILTER_DOES_NOT_HAVE_TAG
    22: 'tags__id__in', // FILTER_HAS_TAGS_ANY

    // Correspondent
    3: 'correspondent__id', // FILTER_CORRESPONDENT
    26: 'correspondent__id__in', // FILTER_HAS_CORRESPONDENT_ANY
    27: 'correspondent__id__none', // FILTER_DOES_NOT_HAVE_CORRESPONDENT

    // Document Type
    4: 'document_type__id', // FILTER_DOCUMENT_TYPE
    28: 'document_type__id__in', // FILTER_HAS_DOCUMENT_TYPE_ANY
    29: 'document_type__id__none', // FILTER_DOES_NOT_HAVE_DOCUMENT_TYPE

    // Storage Path
    25: 'storage_path__id', // FILTER_STORAGE_PATH
    30: 'storage_path__id__in', // FILTER_HAS_STORAGE_PATH_ANY
    31: 'storage_path__id__none', // FILTER_DOES_NOT_HAVE_STORAGE_PATH

    // Workflow Status (document workflow status)
    48: 'workflow_status__id', // FILTER_WORKFLOW_STATUS
    49: 'workflow_status__id__in', // FILTER_HAS_WORKFLOW_STATUS_ANY
    50: 'workflow_status__id__none', // FILTER_DOES_NOT_HAVE_WORKFLOW_STATUS

    // Dates
    43: 'created__date__lte', // FILTER_CREATED_TO
    44: 'created__date__gte', // FILTER_CREATED_FROM
    45: 'added__date__lte', // FILTER_ADDED_TO
    46: 'added__date__gte', // FILTER_ADDED_FROM

    // Text filters
    0: 'title__icontains', // FILTER_TITLE
    19: 'title_content', // FILTER_TITLE_CONTENT
    2: 'archive_serial_number', // FILTER_ASN
    18: 'archive_serial_number__isnull', // FILTER_ASN_ISNULL
    23: 'archive_serial_number__gt', // FILTER_ASN_GT
    24: 'archive_serial_number__lt', // FILTER_ASN_LT
    20: 'query', // FILTER_FULLTEXT_QUERY
    21: 'more_like_id', // FILTER_FULLTEXT_MORELIKE
    36: 'custom_fields__icontains', // FILTER_CUSTOM_FIELDS_TEXT
    42: 'custom_field_query', // FILTER_CUSTOM_FIELDS_QUERY
    47: 'mime_type', // FILTER_MIME_TYPE

    // Permissions
    32: 'owner__id', // FILTER_OWNER
    33: 'owner__id__in', // FILTER_OWNER_ANY
    34: 'owner__isnull', // FILTER_OWNER_ISNULL
    35: 'owner__id__none', // FILTER_OWNER_DOES_NOT_INCLUDE
    37: 'shared_by__id', // FILTER_SHARED_BY_USER
  }

  return filterVarMap[ruleType] || null
}

export interface DocumentsQueryParams {
  page?: number
  page_size?: number
  ordering?: string
  query?: string
  tags__id__all?: string
  tags__id__none?: string
  correspondent__id?: number
  document_type__id?: number
  storage_path__id?: number
  created__date__gt?: string
  created__date__lt?: string
  added__date__gt?: string
  added__date__lt?: string
  [key: string]: any
}

class DocumentService {
  async getDocuments(params?: DocumentsQueryParams): Promise<ApiResponse<Document>> {
    const response = await apiService.get<ApiResponse<Document>>('documents/', {
      params,
    })
    return response.data
  }

  /**
   * Query string for GET /documents/ and GET /documents/selection_summary/
   * (same filter/sort semantics; callers may omit pagination fields for summary).
   */
  buildListQueryParams(
    page?: number,
    pageSize?: number,
    sortField?: string,
    sortReverse?: boolean,
    filterRules?: any[],
    extraParams: any = {}
  ): Record<string, unknown> {
    const params: Record<string, unknown> = {
      page: page || 1,
      page_size: pageSize || 50,
      ...extraParams,
    }

    if (sortField && sortField !== '') {
      params.ordering = sortReverse ? `-${sortField}` : sortField
    }

    if (filterRules && filterRules.length > 0) {
      const groupedRules: { [key: string]: string[] } = {}

      filterRules.forEach((rule) => {
        if (rule.rule_type === 48 && (rule.value == null || rule.value === '-1' || String(rule.value) === '-1')) {
          params['workflow_status__isnull'] = rule.value == null ? 1 : 0
          return
        }
        const filterVar = getFilterVar(rule.rule_type)
        if (filterVar) {
          if (!groupedRules[filterVar]) {
            groupedRules[filterVar] = []
          }
          groupedRules[filterVar].push(rule.value)
        }
      })

      Object.keys(groupedRules).forEach((filterVar) => {
        const values = groupedRules[filterVar]
        if (values.length === 1) {
          params[filterVar] = values[0]
        } else {
          params[filterVar] = values.join(',')
        }
      })
    }

    return params
  }

  async listFiltered(
    page?: number,
    pageSize?: number,
    sortField?: string,
    sortReverse?: boolean,
    filterRules?: any[],
    extraParams: any = {}
  ): Promise<ApiResponse<Document>> {
    const params = this.buildListQueryParams(page, pageSize, sortField, sortReverse, filterRules, extraParams)
    return this.getDocuments(params as DocumentsQueryParams)
  }

  /** Selection aggregates for the current list filters (no document id array). */
  async getSelectionSummary(
    page?: number,
    pageSize?: number,
    sortField?: string,
    sortReverse?: boolean,
    filterRules?: any[],
    extraParams: any = {}
  ): Promise<{
    selected_correspondents: { id: number; document_count: number }[]
    selected_tags: { id: number; document_count: number }[]
    selected_document_types: { id: number; document_count: number }[]
    selected_workflow_statuses: { id: number; document_count: number }[]
    selected_storage_paths: { id: number; document_count: number }[]
    selected_custom_fields: { id: number; document_count: number }[]
  }> {
    const params: Record<string, unknown> = {
      ...this.buildListQueryParams(page, pageSize, sortField, sortReverse, filterRules, extraParams),
    }
    delete params.page
    delete params.page_size
    delete params.ordering
    const response = await apiService.get('documents/selection_summary/', { params })
    return response.data
  }

  async listAllFilteredIds(filterRules?: any[]): Promise<number[]> {
    const response = await this.listFiltered(1, 100000, undefined, undefined, filterRules, { fields: 'id' })
    return response.results.map((doc: Document) => doc.id)
  }

  async getFew(ids: number[], extraParams?: any): Promise<ApiResponse<Document>> {
    const params: any = {
      id__in: ids.join(','),
      ordering: '-id',
      page_size: 1000,
      ...extraParams,
    }

    const response = await apiService.get<ApiResponse<Document>>('documents/', {
      params,
    })
    return response.data
  }

  async getDocument(id: number, fullPerms: boolean = true): Promise<Document> {
    const params = fullPerms ? { full_perms: 'true' } : {}
    const response = await apiService.get<Document>(`documents/${id}/`, {
      params,
    })
    return response.data
  }

  async updateDocument(id: number, data: Partial<Document>): Promise<Document> {
    const response = await apiService.patch<Document>(`documents/${id}/`, data)
    return response.data
  }

  async deleteDocument(id: number): Promise<void> {
    await apiService.delete(`documents/${id}/`)
  }

  async bulkEdit(documentIds: number[], data: Partial<Document>): Promise<void> {
    await apiService.post('documents/bulk_edit/', {
      documents: documentIds,
      ...data,
    })
  }

  async bulkEditOperation(documentIds: number[], method: string, parameters: any = {}): Promise<void> {
    await apiService.post('documents/bulk_edit/', {
      documents: documentIds,
      method: method,
      parameters: parameters,
    })
  }

  async downloadDocument(id: number, original: boolean = false): Promise<Blob> {
    const url = `documents/${id}/preview/`
    const params = original ? { original: 'true' } : {}
    const response = await apiService.get(url, {
      responseType: 'blob',
      params,
    })
    return response.data
  }

  async downloadDocumentForUserAction(id: number, original: boolean = false): Promise<Blob> {
    const url = `documents/${id}/download/`
    const params = original ? {} : { archive: 'true' }
    const response = await apiService.get(url, {
      responseType: 'blob',
      params,
    })
    return response.data
  }

  /** Audit-only: document opened in detail view or user invoked print (not preview blob loads). */
  async recordDocumentAccessEvent(id: number, kind: 'opened' | 'printed'): Promise<void> {
    await apiService.post(`documents/${id}/access_event/`, { kind })
  }

  getThumbUrl(id: number): string {
    return apiService.getApiUrl(`documents/${id}/thumb/`)
  }

  getPreviewUrl(id: number): string {
    return apiService.getApiUrl(`documents/${id}/preview/`)
  }

  async getMetadata(id: number): Promise<any> {
    const response = await apiService.get(`documents/${id}/metadata/`)
    return response.data
  }

  async getSuggestions(id: number): Promise<any> {
    const response = await apiService.get(`documents/${id}/suggestions/`)
    return response.data
  }

  async getHistory(id: number): Promise<any[]> {
    const response = await apiService.get(`documents/${id}/history/`)
    return response.data
  }

  async emailDocuments(
    documentIds: number[],
    emailAddress: string,
    emailSubject: string,
    emailMessage: string,
    useArchiveVersion: boolean = true
  ): Promise<void> {
    await apiService.post('documents/email/', {
      documents: documentIds,
      addresses: emailAddress,
      subject: emailSubject,
      message: emailMessage,
      use_archive_version: useArchiveVersion,
    })
  }

  getDownloadUrl(id: number, original: boolean = false): string {
    const url = apiService.getApiUrl(`documents/${id}/download/`)
    return original ? `${url}?original=true` : url
  }

  async docusignSend(
    documentId: number,
    payload:
      | { client_signers: Array<{ email: string; name?: string }>; company_signers: Array<{ email: string; name?: string }> }
      | { signers: Array<{ email: string; name?: string }> }
      | { signer_email: string; signer_name?: string }
  ): Promise<{ envelope_id: string; status: string }> {
    const response = await apiService.post<{ envelope_id: string; status: string }>(`documents/${documentId}/docusign/send/`, payload)
    return response.data
  }

  async getDocusignEnvelopes(documentId: number): Promise<
    Array<{
      id: number
      envelope_id: string
      status: string
      signer_email: string
      signer_name?: string
      sent_at: string
      created_by_id: number | null
      created_by_username: string | null
      client_signers: Array<{ email: string; name?: string }>
      company_signers: Array<{ email: string; name?: string }>
    }>
  > {
    const response = await apiService.get<
      Array<{
        id: number
        envelope_id: string
        status: string
        signer_email: string
        signer_name?: string
        sent_at: string
        created_by_id: number | null
        created_by_username: string | null
        client_signers: Array<{ email: string; name?: string }>
        company_signers: Array<{ email: string; name?: string }>
      }>
    >(`documents/${documentId}/docusign/envelopes/`)
    return response.data
  }

  async getDocusignConnectLog(documentId: number): Promise<
    Array<{
      id: number
      received_at: string
      envelope_id: string
      event: string
      status_from_payload: string
      previous_status: string
      new_status: string
      matched: boolean
    }>
  > {
    const response = await apiService.get<
      Array<{
        id: number
        received_at: string
        envelope_id: string
        event: string
        status_from_payload: string
        previous_status: string
        new_status: string
        matched: boolean
      }>
    >(`documents/${documentId}/docusign/connect-log/`)
    return response.data
  }

  async getWorkflowNextStatuses(documentId: number): Promise<{
    next_statuses: Array<{ id: number; name: string; color: string; text_color: string }>
    current_status: { id: number; name: string; color: string; text_color: string } | null
    ordered_statuses: Array<{ id: number; name: string; color: string; text_color: string }>
  }> {
    const response = await apiService.get<{
      next_statuses: Array<{ id: number; name: string; color: string; text_color: string }>
      current_status: { id: number; name: string; color: string; text_color: string } | null
      ordered_statuses: Array<{ id: number; name: string; color: string; text_color: string }>
    }>(`documents/${documentId}/workflow-next-statuses/`)
    return response.data
  }

  async getWorkflowAvailableActions(documentId: number): Promise<{
    in_workflow: boolean
    workflow_definition: { id: number; name: string } | null
    status_id?: number | null
    current_step: { id: number; status_id: number; position: number } | null
    available_actions: Array<{
      id: number
      name: string
      next_step_id: number | null
      back_to_step_id: number | null
      end_workflow: boolean
      color?: string
      text_color?: string
      comment_required?: boolean
    }>
    available_definitions: Array<{ id: number; name: string }>
    user_can_claim: boolean
    has_multiple_assignees?: boolean
    user_can_perform_actions?: boolean
    document_type_id: number | null
    steps?: Array<{ id: number; position: number; status_id: number; name?: string }>
    current_step_assignee_usernames?: string[]
    current_step_assignee_group_names?: string[]
  }> {
    const response = await apiService.get<{
      in_workflow: boolean
      workflow_definition: { id: number; name: string } | null
      status_id?: number | null
      current_step: { id: number; status_id: number; position: number } | null
      available_actions: Array<{
        id: number
        name: string
        next_step_id: number | null
        back_to_step_id: number | null
        end_workflow: boolean
        color?: string
        text_color?: string
        comment_required?: boolean
      }>
      available_definitions: Array<{ id: number; name: string }>
      user_can_claim: boolean
      user_can_perform_actions?: boolean
      document_type_id: number | null
    steps?: Array<{ id: number; position: number; status_id: number; name?: string }>
    current_step_assignee_usernames?: string[]
    current_step_assignee_group_names?: string[]
  }>(`documents/${documentId}/workflow-available-actions/`)
    return response.data
  }

  async workflowAdvance(
    documentId: number,
    payload:
      | { action_id: number; assign_user_ids?: number[]; assign_group_ids?: number[]; comment?: string }
      | { start_workflow_definition_id: number }
      | { next_status_id: number; assign_user_ids?: number[]; assign_group_ids?: number[]; comment?: string }
      | {
          steps: Array<{
            next_status_id: number
            assign_user_ids?: number[]
            assign_group_ids?: number[]
            comment?: string
          }>
        }
  ): Promise<{ status: string; workflow_status: number; message: string; end_workflow?: boolean }> {
    const response = await apiService.post<{
      status: string
      workflow_status: number
      message: string
      end_workflow?: boolean
    }>(`documents/${documentId}/workflow-advance/`, payload)
    return response.data
  }

  async getWorkflowHistory(documentId: number): Promise<
    Array<{
      entry_type: 'status_change' | 'claim' | 'unclaim'
      performed_at: string
      performed_by_username: string | null
      from_status_id: number | null
      from_status_name: string | null
      to_status_id: number | null
      to_status_name: string | null
      workflow_definition_id: number | null
      workflow_definition_name: string | null
      assigned_user_ids: number[]
      assigned_group_ids: number[]
      comment: string
    }>
  > {
    const response = await apiService.get<
      Array<{
        entry_type: 'status_change' | 'claim' | 'unclaim'
        performed_at: string
        performed_by_username: string | null
        from_status_id: number | null
        from_status_name: string | null
        to_status_id: number | null
        to_status_name: string | null
        workflow_definition_id: number | null
        workflow_definition_name: string | null
        assigned_user_ids: number[]
        assigned_group_ids: number[]
        comment: string
      }>
    >(`documents/${documentId}/workflow-history/`)
    return response.data
  }

  async claimDocument(documentId: number): Promise<{ status: string; message: string }> {
    const response = await apiService.post<{ status: string; message: string }>(
      `documents/${documentId}/claim/`
    )
    return response.data
  }

  async unclaimDocument(documentId: number): Promise<{ status: string; message: string }> {
    const response = await apiService.post<{ status: string; message: string }>(
      `documents/${documentId}/unclaim/`
    )
    return response.data
  }

  async downloadDocumentFile(id: number, original: boolean = false, filename?: string, archive: boolean = false): Promise<void> {
    const url = `documents/${id}/download/`
    const params = archive ? { archive: 'true' } : original ? { original: 'true' } : {}

    try {
      const response = await apiService.get(url, {
        responseType: 'blob',
        params,
      })

      const blob = response.data as Blob
      const contentDisposition = response.headers['content-disposition']
      let downloadFilename = filename

      if (!downloadFilename && contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '')
        }
      }

      if (!downloadFilename) {
        downloadFilename = `document-${id}${original ? '-original' : ''}`
      }

      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = downloadFilename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      throw error
    }
  }

  async getVersions(documentId: number): Promise<DocumentVersion[]> {
    const response = await apiService.get<DocumentVersion[]>(`documents/${documentId}/versions/`)
    return response.data
  }

  async uploadVersion(documentId: number, file: File, comment?: string): Promise<void> {
    const formData = new FormData()
    formData.append('document', file, file.name)
    if (comment) {
      formData.append('comment', comment)
    }

    await apiService.post(`documents/${documentId}/versions/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async restoreVersion(documentId: number, versionId: number): Promise<DocumentVersion> {
    const response = await apiService.post<DocumentVersion>(`documents/${documentId}/versions/${versionId}/restore/`)
    return response.data
  }

  async getSelectionData(documentIds: number[]): Promise<{
    selected_correspondents: { id: number; document_count: number }[]
    selected_tags: { id: number; document_count: number }[]
    selected_document_types: { id: number; document_count: number }[]
    selected_storage_paths: { id: number; document_count: number }[]
    selected_custom_fields: { id: number; document_count: number }[]
  }> {
    const response = await apiService.post('documents/selection_data/', {
      documents: documentIds,
    })
    return response.data
  }

  async bulkDownload(documentIds: number[], content: 'archive' | 'originals' | 'both' = 'both', followFormatting: boolean = false): Promise<Blob> {
    const response = await apiService.post(
      'documents/bulk_download/',
      {
        documents: documentIds,
        content: content,
        follow_formatting: followFormatting,
      },
      {
        responseType: 'blob',
      }
    )
    return response.data
  }

  async getNextAsn(): Promise<number> {
    const response = await apiService.get<number>('documents/next_asn/')
    return response.data
  }

  async getRelatedItems(documentId: number): Promise<DocumentRelatedItem[]> {
    const response = await apiService.get<DocumentRelatedItem[]>(`documents/${documentId}/related-items/`)
    return response.data
  }

  async addRelatedItem(documentId: number, targetDocumentId: number): Promise<{ created: boolean; item: DocumentRelatedItem }> {
    const response = await apiService.post<{ created: boolean; item: DocumentRelatedItem }>(
      `documents/${documentId}/related-items/`,
      {
        target_document_id: targetDocumentId,
      }
    )
    return response.data
  }

  async removeRelatedItem(documentId: number, targetDocumentId: number): Promise<void> {
    await apiService.delete(`documents/${documentId}/related-items/${targetDocumentId}/`)
  }
}

export const documentService = new DocumentService()
